import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { FaWhatsapp } from "react-icons/fa";

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  defaultOpen?: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200  rounded-lg overflow-hidden mb-4 shadow-sm hover:shadow-md transition-shadow">
      <button
        className="w-full px-4 sm:px-6 py-4 hover:cursor-pointer sm:py-5 text-left flex justify-between items-center focus:outline-none bg-white"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-gray-800 text-base sm:text-lg pr-2">{question}</span>
        {isOpen ? (
          <FiChevronUp className="text-[#1c9e86] flex-shrink-0 ml-2" />
        ) : (
          <FiChevronDown className="text-[#1c9e86] flex-shrink-0 ml-2" />
        )}
      </button>
      <div 
        className={`transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 sm:px-6 py-4 sm:py-5 bg-[#e9f5f2] text-gray-600">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const faqs = [
    {
      question: "How do I open an EximPe Global Collection Account?",
      answer: (
        <>
          Visit <a href="https://app.eximpe.com/auth/sign-up" className="text-[#1c9e86] hover:underline font-medium">our sign-up page</a> to create an account. Complete KYC verification to activate your account within 48 hours.
        </>
      ),
      defaultOpen: true
    },
    {
      question: "What currencies are supported?",
      answer: "We support 30+ currencies including USD, EUR, GBP, and more across 100+ countries. Our extensive currency support allows businesses to transact globally without worrying about exchange limitations.",
    },
    {
      question: "What are the charges?",
      answer: (
        <div className="space-y-2">
          <p className="font-medium">Our transparent fee structure includes:</p>
          <ul className="list-disc list-inside space-y-1 pl-4">
            <li>Platform fee: 0.75%</li>
            <li>Local collection fee: 0.59 in transaction currency</li>
            <li>SWIFT transfers: $7.67 per transaction</li>
          </ul>
          <p className="mt-2 text-sm italic">All fees are significantly lower than traditional banking channels.</p>
        </div>
      ),
    },
    {
      question: "Can I keep funds in my global account?",
      answer: "No, funds are automatically converted to INR and transferred to your Indian account daily. This ensures you receive your money quickly and don't have idle funds sitting in your global account.",
    },
    {
      question: "How do I receive money?",
      answer: "Share your global account details with clients. Funds will be converted and credited to your Indian account within 24-48 hours with e-FIRA. The entire process is automated and requires no manual intervention after the initial setup.",
    },
  ];

  return (
    <section id="faq" className="py-16 sm:py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl  mx-auto">
          <div className="text-center  mb-12 sm:mb-16">
            <div className="inline-block px-4 py-1 rounded-full bg-[#ecf6f3] text-[#1c9e86] font-medium text-sm mb-4">
              Got Questions?
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Everything you need to know about our global account services
            </p>
          </div>
          
          <div className="space-y-4 ">
            {faqs.map((faq, index) => (
              <FAQItem 
                key={index} 
                question={faq.question} 
                answer={faq.answer}
                defaultOpen={faq.defaultOpen}
              />
            ))}
          </div>
          
          {/* Additional help section */}
          <div className="mt-12 p-6 sm:p-8 bg-[#ecf6f3] rounded-xl text-center">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Still have questions?</h3>
            <p className="text-gray-600 mb-6">
              Our team is here to help. Contact us for personalized assistance.
            </p>
            <a href='https://api.whatsapp.com/send?phone=916909016944&text=Hello,%20I%20have%20a%20question.'>
            <button className="bg-[#1c9e86] hover:cursor-pointer hover:bg-[#168a75] text-white font-bold py-3 px-8 rounded-lg transition duration-300 shadow-lg inline-flex items-center">
              <FaWhatsapp className="mr-2 text-2xl" />
              Contact Support
            </button>
            </a>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;